<!--
    Página pública de planes (§3.4 de
    ../creciente-fertil-app/docs/suscripciones-flujo-propuesto.md).

    Es "la web de Netflix": existe, cualquiera la encuentra, y LA APP NO LA
    LINKEA desde ninguna pantalla interna — en ninguna plataforma. Esa asimetría
    es el diseño, no un olvido: la app muestra estado y esta página vende. Si
    algún día aparece un link a /planes dentro de la app, se rompe la guideline
    3.1.3(d) de Apple para las tres plataformas a la vez.

    Se llega por el mail que manda el backend cuando el usuario toca un límite
    (Etapa 4), o googleando.
-->
<script>
    import { onMount } from "svelte";
    import { goto } from "$app/navigation";

    import { getUser } from "$lib/userstorage/usersotrage";
    import {
        crearSuscripcion,
        crearSuscripcionPorLink,
        getMiSuscripcion,
        getPlanes,
    } from "$lib/java/suscripciones/suscripcionesback";
    import {
        esPlanActual,
        limitesDePlan,
        ordenarPlanes,
        planesContratables,
        precioMostrable,
    } from "$lib/suscripciones/catalogoPlanes";
    import { AskSwal, ErrorSwal } from "$lib/stringutil/lib";
    import { guardarDestino } from "$lib/auth/destinoPostLogin";
    import { leerYLimpiarToken } from "$lib/suscripciones/linkDeCompra";
    import {
        guardarPlanPendiente,
        leerPlanPendiente,
        olvidarPlanPendiente,
    } from "$lib/suscripciones/planPendiente";

    const pre = import.meta.env.VITE_PRE;

    let planes = $state([]);
    let cargando = $state(true);
    let error = $state(false);
    let haySesion = $state(false);
    let procesando = $state(null);
    let pendiente = $state(null);
    // El plan que ya tiene, para no ofrecerle comprar lo que esta pagando.
    let miSuscripcion = $state(null);
    // La llave que trae el link del mail: deja contratar sin sesion en este
    // navegador. Ver `crearSuscripcionPorLink`.
    let linkToken = $state(null);

    let visibles = $derived(ordenarPlanes(planesContratables(planes)));
    // Se puede comprar sin login cuando el link del mail trajo su llave.
    let puedeComprar = $derived(haySesion || !!linkToken);

    onMount(async () => {
        haySesion = !!getUser()?.token;
        linkToken = leerYLimpiarToken();
        if (haySesion) {
            pendiente = leerPlanPendiente();
        }
        try {
            planes = await getPlanes();
        } catch (err) {
            error = true;
            console.error(err);
        } finally {
            cargando = false;
        }

        if (!haySesion) return;

        // Que no le vendamos lo que ya tiene. Va aparte del catalogo y sin
        // romper la pagina si falla: /planes carga sin sesion, asi que esto es
        // un extra, no un requisito.
        try {
            miSuscripcion = await getMiSuscripcion();
        } catch (err) {
            console.error(err);
        }

        // Volvio del login para terminar de contratar: se sigue solo. Antes
        // habia que tocar "Continuar" despues de haber elegido el plan y
        // haberse logueado, o sea un tercer paso para repetir algo que ya
        // habia decidido. El cartel queda igual como red por si esto falla.
        const retomar = pendiente && planPendiente();
        if (retomar && !esPlanActual(retomar, miSuscripcion)) {
            elegir(retomar);
        }
    });

    /** Manda al login recordando el plan y a dónde volver. */
    function alLogin(plan) {
        // El cartel de "Continuar" queda como red: si el regreso automático
        // fallara (storage bloqueado, el usuario navega a otro lado), la
        // intención no se pierde.
        guardarPlanPendiente(plan.id);
        guardarDestino("/planes");
        goto(pre + "/login");
    }

    async function elegir(plan) {
        // Sin sesión y sin llave del mail no hay forma de saber de quién sería
        // la compra: al login.
        if (!haySesion && !linkToken) {
            alLogin(plan);
            return;
        }

        procesando = plan.id;
        try {
            // Con sesión gana la sesión; la llave es para quien no la tiene.
            const { initPoint } = haySesion
                ? await crearSuscripcion(plan.id)
                : await crearSuscripcionPorLink(linkToken, plan.id);
            if (!initPoint) throw new Error("Mercado Pago no devolvió el link de pago");
            olvidarPlanPendiente();
            // Al checkout de MP. El plan NO queda asignado acá: lo confirma el
            // webhook cuando MP cobra.
            window.location.href = initPoint;
        } catch (err) {
            // Llave vencida o desconocida (404): es el mail viejo que alguien
            // abre tres semanas después. No es un error que mostrar —no hizo
            // nada mal— así que se sigue por el camino de siempre, el login.
            if (!haySesion && linkToken && err?.status === 404) {
                linkToken = null;
                procesando = null;
                alLogin(plan);
                return;
            }
            if (err?.code === "ACCOUNT_NOT_VALIDATED") {
                procesando = null;
                const resp = await AskSwal(
                    "Confirmá tu email primero",
                    "Todavía no validaste tu cuenta. Confirmá tu email para poder contratar este plan.",
                    "warning",
                    true,
                    "Ir a confirmar",
                    "Cerrar",
                );
                if (resp.isConfirmed) goto(pre + "/user/verify-email");
                return;
            }
            // El cartel es genérico a propósito —el motivo que manda Mercado
            // Pago no está escrito para el usuario— pero tiene que quedar en la
            // consola: un checkout que falla sin dejar rastro en ningún lado
            // costó una tarde de diagnóstico.
            console.error("No se pudo iniciar el checkout:", err);
            ErrorSwal(
                "No pudimos iniciar el pago",
                "Intentá de nuevo en un momento.",
                err,
            );
            procesando = null;
        }
    }

    function planPendiente() {
        return visibles.find((p) => p.id === pendiente);
    }
</script>

<svelte:head>
    <title>Planes · Creciente Fértil</title>
    <meta
        name="description"
        content="Planes de Creciente Fértil: gestión de tu rodeo con límites según el tamaño de tu establecimiento."
    />
</svelte:head>

<div class="min-h-screen bg-cf-bg p-4 py-12">
    <div class="mx-auto w-full max-w-5xl">
        <header class="text-center mb-10">
            <h1 class="t-display mb-3">Planes de Creciente Fértil</h1>
            <p class="t-body text-cf-text-sub max-w-2xl mx-auto">
                Elegí el plan según el tamaño de tu rodeo. Podés cambiar o dar de
                baja cuando quieras, y tus datos siempre siguen siendo tuyos.
            </p>
        </header>

        {#if pendiente && planPendiente()}
            <div class="mb-8 rounded-xl border border-cf-primary bg-cf-surface p-4 text-center">
                <p class="t-body">
                    Estabas por contratar el plan <strong>{planPendiente().nombre}</strong>.
                </p>
                <button
                    class="mt-3 px-5 py-2 rounded-lg bg-cf-primary text-white disabled:opacity-50"
                    disabled={procesando !== null}
                    onclick={() => elegir(planPendiente())}
                >
                    {#if procesando !== null}
                        Te llevamos a Mercado Pago…
                    {:else}
                        Continuar
                    {/if}
                </button>
            </div>
        {/if}

        {#if cargando}
            <p class="text-center t-body text-cf-text-sub">Cargando planes…</p>
        {:else if error}
            <div class="text-center">
                <p class="t-body text-cf-danger">
                    No pudimos cargar los planes en este momento.
                </p>
                <button
                    class="mt-4 px-4 py-2 rounded-lg bg-cf-primary text-white"
                    onclick={() => location.reload()}
                >
                    Reintentar
                </button>
            </div>
        {:else}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {#each visibles as plan}
                    {@const precio = precioMostrable(plan)}
                    {@const actual = esPlanActual(plan, miSuscripcion)}
                    <div
                        class="bg-cf-surface rounded-xl shadow p-6 flex flex-col"
                        class:ring-2={actual}
                        class:ring-cf-primary={actual}
                    >
                        <div class="flex items-center justify-between gap-2">
                            <h2 class="t-card">{plan.nombre}</h2>
                            {#if actual}
                                <span
                                    class="text-xs px-2 py-1 rounded-full bg-cf-primary text-white whitespace-nowrap"
                                >
                                    Tu plan
                                </span>
                            {/if}
                        </div>

                        {#if precio !== null}
                            <p class="mt-3">
                                <span class="t-num-lg">USD {precio}</span>
                                <span class="t-body text-cf-text-sub">/mes</span>
                            </p>
                        {/if}

                        <ul class="mt-5 space-y-2 flex-1">
                            {#each limitesDePlan(plan) as limite}
                                <li class="t-body flex justify-between gap-3">
                                    <span class="text-cf-text-sub">{limite.etiqueta}</span>
                                    <span>{limite.valor}</span>
                                </li>
                            {/each}
                        </ul>

                        <button
                            class="mt-6 w-full py-2.5 rounded-lg bg-cf-primary text-white disabled:opacity-50"
                            disabled={procesando !== null || actual}
                            onclick={() => elegir(plan)}
                        >
                            {#if actual}
                                Es el que tenés
                            {:else if procesando === plan.id}
                                Redirigiendo…
                            {:else if puedeComprar}
                                Elegir plan
                            {:else}
                                Iniciar sesión para contratar
                            {/if}
                        </button>
                    </div>
                {/each}
            </div>

            <!-- Decisión de negocio #2: se expresa en dólares y se cobra en
                 pesos. La aclaración es obligatoria para que nadie se sorprenda
                 con el monto del resumen. -->
            <p class="mt-8 text-center t-value text-cf-text-sub">
                Los precios están expresados en dólares estadounidenses. El cobro
                se realiza en pesos argentinos a través de Mercado Pago.
            </p>
        {/if}
    </div>
</div>
